import { moveItemInArray } from '@angular/cdk/drag-drop';
import { computed, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { LassiLayer } from '@core/api/angular';
import {
  bufferTime,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import {
  ActiveLayers,
  LassiCustomPreset,
  LassiLayersList,
  LassiProfiles,
  MapLayer,
  parseUrlLayers,
  stringifyUrlLayers,
} from './layers';
import {
  BASE_LAYER_QUERY_PARAM_KEY,
  LAYER_QUERY_PARAM_KEY,
  PROFILE_QUERY_PARAM_KEY,
} from './map';

@Injectable({
  providedIn: 'root',
})
export class LayerManagementService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly lassiLayersList$ = inject(LassiLayersList);

  readonly lassiLayers$ = this.lassiLayersList$.pipe(
    map((payload) => payload.layers),
  );

  private readonly defaultOpacity = 1;

  public readonly urlBaseLayer$ = this.activatedRoute.queryParamMap.pipe(
    map((params) => params.get(BASE_LAYER_QUERY_PARAM_KEY)),
    filter(Boolean),
    distinctUntilChanged(),
    shareReplay(1),
  );

  public changeBaseLayer(baseLayer: string) {
    return this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        [BASE_LAYER_QUERY_PARAM_KEY]: baseLayer,
      },
      queryParamsHandling: 'merge',
    });
  }

  readonly urlLayersUnparsed$ = this.activatedRoute.queryParamMap.pipe(
    map((params) => params.get(LAYER_QUERY_PARAM_KEY)),
    distinctUntilChanged(),
  );

  private readonly urlLayers$ = this.urlLayersUnparsed$.pipe(
    map((str) => parseUrlLayers(str)),
  );

  public readonly currentProfile$ = this.activatedRoute.queryParamMap.pipe(
    map(
      (params) => params.get(PROFILE_QUERY_PARAM_KEY) as LassiProfiles | null,
    ),
    map((profile) => profile),
    distinctUntilChanged(),
  );

  private readonly isNamedProfile$ = this.currentProfile$.pipe(
    map((profile) => profile && profile !== 'Custom'),
  );

  private readonly defaultLayers$ = this.lassiLayersList$.pipe(
    map((payload) => payload.presets),
  );

  readonly lassiLayersMap$ = this.lassiLayers$.pipe(
    map((layerGroups) => {
      return new Map(
        layerGroups
          .flatMap((layerGroup) =>
            layerGroup.layers.map((layer) => ({
              ...layer,
              group: layerGroup.name,
            })),
          )
          .map((layer) => [layer.name, layer] as [string, MapLayer]),
      );
    }),
    shareReplay(1),
  );

  private readonly customPreset = inject(LassiCustomPreset);

  private readonly customLayerList$ = toObservable(
    computed(() =>
      this.customPreset.hasValue()
        ? this.customPreset.value()?.custom_preset
        : null,
    ),
  );

  public readonly isModified$ = combineLatest([
    this.isNamedProfile$,
    this.urlLayers$,
    this.customLayerList$,
  ]).pipe(
    map(([isNamedProfile, urlLayers, customLayers]) => {
      if (isNamedProfile) {
        return !!urlLayers;
      }

      return stringifyUrlLayers(urlLayers ?? []) !== customLayers;
    }),
  );

  autoSwitchLayers(prev: number, cur: number) {
    return combineLatest([this.currentProfile$, this.defaultLayers$]).pipe(
      map(([currentProfile, presets]) =>
        presets.find((preset) => preset.name === currentProfile),
      ),
      switchMap((preset) => {
        if (!preset) {
          return of(null);
        }

        return merge(
          preset.layers
            .filter((layer) => layer.autoVisibleSwitchScale)
            .filter((layer) => {
              const threshold = layer.autoVisibleSwitchScale!;
              return (
                (prev < threshold && cur >= threshold) || // crossed upwards
                (prev >= threshold && cur < threshold) // crossed downwards
              );
            })
            .map((layer) => ({
              layer,
              direction: cur > prev ? 'up' : 'down',
              prev,
              cur,
            })),
        ).pipe(
          bufferTime(500),
          filter((batch) => batch.length > 0), // skip empty batches
          map((batch) =>
            batch.reduce(
              (
                acc,
                {
                  layer: {
                    name: layerName,
                    autoVisibleSwitchScale: threshold,
                    displayGreaterThanSwitchScale,
                    displayLessThanSwitchScale,
                  },
                  cur: scale,
                },
              ) => {
                if (scale > threshold! && displayLessThanSwitchScale !== null) {
                  acc[layerName] = displayLessThanSwitchScale;
                } else if (
                  scale < threshold! &&
                  displayGreaterThanSwitchScale !== null
                ) {
                  acc[layerName] = displayGreaterThanSwitchScale;
                }
                return acc;
              },
              {} as Record<string, boolean>,
            ),
          ),
          switchMap((batch) => this.toggleLayers(batch)),
        );
      }),
    );
  }

  public activeLayers$: Observable<ActiveLayers> = combineLatest([
    this.currentProfile$,
    this.urlLayers$,
    this.lassiLayersMap$,
  ]).pipe(
    switchMap(([currentProfile, urlLayers, layersMap]) => {
      if (currentProfile === 'Custom' || urlLayers) {
        return of(urlLayers ?? []);
      }

      return this.defaultLayers$.pipe(
        take(1),
        map((defaultLayers) => {
          if (!currentProfile) {
            return defaultLayers[0]?.layers.map((layer) => layer.name) ?? [];
          }

          return (
            defaultLayers
              .find((preset) => preset.name === currentProfile)
              ?.layers.map((layer) => ({
                name: layer.name,
                zIndex: layersMap.get(layer.name)?.zIndex ?? 0,
              }))
              .sort((a, b) => b.zIndex - a.zIndex)
              .map(({ name }) => name) ?? []
          );
        }),
        map((layers) =>
          layers.map((layer) => ({
            name: layer,
            status: true,
            opacity: this.defaultOpacity,
          })),
        ),
      );
    }),
    shareReplay(1),
  );

  public changeCustomProfile(layers: string | null) {
    return this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        [PROFILE_QUERY_PARAM_KEY]: 'Custom',
        [LAYER_QUERY_PARAM_KEY]: layers,
      },
      queryParamsHandling: 'merge',
    });
  }

  public changeProfile(profile: string) {
    return this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        [PROFILE_QUERY_PARAM_KEY]: profile,
        [LAYER_QUERY_PARAM_KEY]: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  private changeUrlLayers(layers: ActiveLayers) {
    return this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        [LAYER_QUERY_PARAM_KEY]: stringifyUrlLayers(layers),
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private async getBaseLayers() {
    const urlLayers = await firstValueFrom(this.urlLayers$);
    const activeLayers = await firstValueFrom(this.activeLayers$);
    return urlLayers ?? activeLayers;
  }

  public activeMapLayers$ = combineLatest([
    this.activeLayers$,
    this.lassiLayersMap$,
  ]).pipe(
    map(([layers, layersMap]) =>
      layers
        .map(({ name, status, opacity }) => {
          const layer = layersMap.get(name);
          if (!layer) {
            return undefined;
          }
          return {
            ...layer,
            status,
            opacity,
          };
        })
        .filter((layer) => layer !== undefined),
    ),
  );

  /**
   * Gets the layer names for a named preset (e.g., 'SMES', 'VICNAMES').
   * Returns an empty array if the preset is not found.
   */
  public getPresetLayerNames(presetName: string): Observable<string[]> {
    return this.defaultLayers$.pipe(
      map((presets) => {
        const preset = presets.find((p) => p.name === presetName);
        return preset?.layers.map((layer) => layer.name) ?? [];
      }),
    );
  }

  public async addLayer(layer: LassiLayer, opacity = this.defaultOpacity) {
    const layersMap = await firstValueFrom(this.lassiLayersMap$);

    const existingLayers = (await this.getBaseLayers()).reverse();

    const insertIndex = existingLayers.findIndex(({ name }) => {
      return (layersMap.get(name)?.zIndex ?? 0) > layer.zIndex;
    });

    const newActiveLayer = {
      name: layer.name,
      status: true,
      opacity,
    };
    if (insertIndex === -1) {
      existingLayers.push(newActiveLayer);
    } else {
      existingLayers.splice(insertIndex, 0, newActiveLayer);
    }

    const newUrlLayers = existingLayers.reverse();
    return this.changeUrlLayers(newUrlLayers);
  }

  public async addLayers(layers: LassiLayer[], opacity = this.defaultOpacity) {
    const layersMap = await firstValueFrom(this.lassiLayersMap$);
    const existingLayers = (await this.getBaseLayers()).reverse();
    const existingNames = new Set(existingLayers.map((l) => l.name));

    for (const layer of layers) {
      // Skip layers already in the active list
      if (existingNames.has(layer.name)) {
        continue;
      }

      const insertIndex = existingLayers.findIndex(
        ({ name }) => (layersMap.get(name)?.zIndex ?? 0) > layer.zIndex,
      );

      const newActiveLayer = { name: layer.name, status: true, opacity };
      if (insertIndex === -1) {
        existingLayers.push(newActiveLayer);
      } else {
        existingLayers.splice(insertIndex, 0, newActiveLayer);
      }
      existingNames.add(layer.name);
    }

    // Single URL update for all added layers
    existingLayers.reverse();
    return this.changeUrlLayers(existingLayers);
  }

  public async removeLayer(layerName: LassiLayer['name']) {
    const newLayers = (await this.getBaseLayers()).filter(
      (layer) => layer.name !== layerName,
    );

    return this.changeUrlLayers(newLayers);
  }

  public async removeLayers(layerNames: LassiLayer['name'][]) {
    const namesToRemove = new Set(layerNames);
    const newLayers = (await this.getBaseLayers()).filter(
      (layer) => !namesToRemove.has(layer.name),
    );

    return this.changeUrlLayers(newLayers);
  }

  public async toggleLayers(actions: Record<LassiLayer['name'], boolean>) {
    const newLayers = (await this.getBaseLayers()).map((layer) => {
      const newStatus = actions[layer.name];
      if (newStatus === undefined) {
        return layer;
      }
      return {
        ...layer,
        status: newStatus,
      };
    });

    return this.changeUrlLayers(newLayers);
  }

  public async toggleLayer(layerName: LassiLayer['name'], status?: boolean) {
    const newLayers = (await this.getBaseLayers()).map((layer) =>
      layer.name === layerName
        ? {
            ...layer,
            status: status ?? !layer.status,
          }
        : layer,
    );

    return this.changeUrlLayers(newLayers);
  }

  public async updateLayerOpacity(
    layerName: LassiLayer['name'],
    opacity: number,
  ) {
    const newLayers = (await this.getBaseLayers()).map((layer) =>
      layer.name === layerName
        ? {
            ...layer,
            opacity,
          }
        : layer,
    );

    return this.changeUrlLayers(newLayers);
  }

  public removeAllLayers() {
    return this.changeUrlLayers([]);
  }

  public async reorderLayers(previousIndex: number, currentIndex: number) {
    const baseLayers = await this.getBaseLayers();
    moveItemInArray(baseLayers, previousIndex, currentIndex);

    return this.changeUrlLayers(baseLayers);
  }
}
